import {
  apiSourceConfigSchema,
  minimumPlanFor,
  PLAN_LABEL,
  type ApiPreview,
  type ApiSourceConfig,
  type EntitlementsView,
} from '@agents/shared';
import { useState } from 'react';
import { Alert, Button, Field, problemMessage } from './ui';
import { usePreviewConnector } from '../lib/queries';

/** Flat form state for an API connector; converted to the API shape by `toConfig`. */
export interface ApiForm {
  endpoint: string;
  authType: 'none' | 'header' | 'bearer';
  headerName: string;
  secret: string;
  path: string;
  id: string;
  title: string;
  text: string;
  link: string;
  pagination: 'none' | 'page' | 'next';
  param: string;
  nextPath: string;
  maxPages: string;
  refresh: 'manual' | '6' | '24' | '168';
}

export function apiFormFrom(cfg?: ApiSourceConfig | null): ApiForm {
  return {
    endpoint: cfg?.endpoint ?? '',
    authType: cfg?.auth.type ?? 'bearer',
    headerName: cfg?.auth.headerName ?? 'X-API-Key',
    secret: '',
    path: cfg?.items.path ?? '',
    id: cfg?.items.id ?? 'id',
    title: cfg?.items.title ?? 'name',
    text: cfg?.items.text.join(', ') ?? '',
    link: cfg?.items.link ?? '',
    pagination: cfg?.pagination.type ?? 'none',
    param: cfg?.pagination.param ?? 'page',
    nextPath: cfg?.pagination.nextPath ?? 'next',
    maxPages: String(cfg?.pagination.maxPages ?? 10),
    refresh: cfg?.refreshHours ? (String(cfg.refreshHours) as ApiForm['refresh']) : 'manual',
  };
}

export function toConfig(f: ApiForm) {
  return {
    endpoint: f.endpoint.trim(),
    auth: { type: f.authType, ...(f.authType === 'header' ? { headerName: f.headerName.trim() } : {}) },
    headers: {},
    items: {
      path: f.path.trim(),
      id: f.id.trim(),
      title: f.title.trim(),
      text: f.text
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean),
      ...(f.link.trim() ? { link: f.link.trim() } : {}),
    },
    pagination: {
      type: f.pagination,
      maxPages: Number(f.maxPages) || 1,
      ...(f.pagination === 'page' ? { param: f.param.trim() } : {}),
      ...(f.pagination === 'next' ? { nextPath: f.nextPath.trim() } : {}),
    },
    refreshHours: f.refresh === 'manual' ? null : Number(f.refresh),
  };
}

/** zod issues -> form field keys used by `ApiConnectorFields`. */
export function apiErrors(issues: { path: PropertyKey[]; message: string }[]): Record<string, string> {
  const map: Record<string, string> = {
    'api.endpoint': 'endpoint',
    'api.auth.headerName': 'headerName',
    'api.items.path': 'path',
    'api.items.id': 'id',
    'api.items.title': 'title',
    'api.items.text': 'text',
    'api.items.link': 'link',
    'api.pagination.param': 'param',
    'api.pagination.nextPath': 'nextPath',
    'api.pagination.maxPages': 'maxPages',
    apiSecret: 'secret',
  };
  const out: Record<string, string> = {};
  for (const i of issues) {
    const key = i.path.map(String).join('.');
    const field = map[key] ?? Object.entries(map).find(([k]) => key.startsWith(k))?.[1];
    if (field && !out[field]) out[field] = i.message;
  }
  return out;
}

const REFRESH: { value: ApiForm['refresh']; label: string; hours: number }[] = [
  { value: 'manual', label: 'Manual', hours: 0 },
  { value: '6', label: 'Cada 6 horas', hours: 6 },
  { value: '24', label: 'Diaria', hours: 24 },
  { value: '168', label: 'Semanal', hours: 168 },
];

export function ApiConnectorFields({
  form,
  errors,
  ent,
  hasStoredSecret,
  sourceId,
  onChange,
}: {
  form: ApiForm;
  errors: Record<string, string>;
  ent: EntitlementsView | undefined;
  hasStoredSecret?: boolean;
  sourceId?: string;
  onChange: (patch: Partial<ApiForm>) => void;
}) {
  const previewer = usePreviewConnector();
  const [preview, setPreview] = useState<ApiPreview | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const planMin = ent?.values['knowledge.api.minRefreshHours'] ?? 0;

  const text = (
    key: keyof ApiForm,
    label: string,
    opts: { help?: string; placeholder?: string; optional?: boolean; type?: string } = {},
  ) => (
    <Field label={label} optional={opts.optional} help={opts.help} error={errors[key]}>
      {(p) => (
        <input
          {...p}
          className="input"
          type={opts.type ?? 'text'}
          autoComplete="off"
          spellCheck={false}
          placeholder={opts.placeholder}
          value={form[key]}
          onChange={(e) => onChange({ [key]: e.target.value } as Partial<ApiForm>)}
        />
      )}
    </Field>
  );

  const runPreview = async () => {
    setPreview(null);
    setPreviewError(null);
    const parsed = apiSourceConfigSchema.safeParse(toConfig(form));
    if (!parsed.success) {
      setPreviewError('Completa los campos marcados antes de probar la conexión.');
      return;
    }
    try {
      setPreview(
        await previewer.mutateAsync({
          api: parsed.data,
          ...(form.secret ? { apiSecret: form.secret } : {}),
          ...(sourceId ? { sourceId } : {}),
        }),
      );
    } catch (err) {
      setPreviewError(problemMessage(err, 'No se pudo probar la conexión.'));
    }
  };

  return (
    <>
      {text('endpoint', 'Dirección de la API', {
        type: 'url',
        placeholder: 'https://api.tuempresa.com/v1/productos',
        help: 'Solo GET sobre https. Leemos la respuesta en JSON.',
      })}
      <fieldset className="choice-group">
        <legend>Autenticación</legend>
        {(
          [
            ['bearer', 'Token Bearer', 'Se envía como Authorization: Bearer …'],
            ['header', 'API key en un encabezado', 'Por ejemplo X-API-Key.'],
            ['none', 'Sin autenticación', 'API pública.'],
          ] as const
        ).map(([value, label, help]) => (
          <label key={value} className="choice">
            <input
              type="radio"
              name="authType"
              checked={form.authType === value}
              onChange={() => onChange({ authType: value })}
            />
            <span>
              <strong>{label}</strong>
              <small>{help}</small>
            </span>
            <span />
          </label>
        ))}
      </fieldset>
      {form.authType === 'header' &&
        text('headerName', 'Nombre del encabezado', { placeholder: 'X-API-Key' })}
      {form.authType !== 'none' &&
        text('secret', 'Credencial', {
          type: 'password',
          placeholder: hasStoredSecret ? '•••••••• guardada; escribe para reemplazarla' : '',
          help: 'Se guarda cifrada y nunca se vuelve a mostrar.',
        })}

      <fieldset className="choice-group">
        <legend>Qué leer de la respuesta</legend>
        {text('path', 'Ruta de la lista', {
          optional: true,
          placeholder: 'data.items',
          help: 'Vacío si la respuesta ya es una lista.',
        })}
        {text('id', 'Campo identificador', { placeholder: 'id' })}
        {text('title', 'Campo título', { placeholder: 'name' })}
        {text('text', 'Campos con la información', {
          placeholder: 'description, price, category',
          help: 'Separados por comas. Usa puntos para campos anidados.',
        })}
        {text('link', 'Campo enlace', { optional: true, placeholder: 'url' })}
      </fieldset>

      <Field label="Paginación">
        {(p) => (
          <select
            {...p}
            className="input"
            value={form.pagination}
            onChange={(e) => onChange({ pagination: e.target.value as ApiForm['pagination'] })}
          >
            <option value="none">Una sola página</option>
            <option value="page">Número de página en la URL</option>
            <option value="next">Enlace a la siguiente página en la respuesta</option>
          </select>
        )}
      </Field>
      {form.pagination === 'page' && text('param', 'Parámetro de página', { placeholder: 'page' })}
      {form.pagination === 'next' &&
        text('nextPath', 'Campo con la siguiente página', { placeholder: 'links.next' })}
      {form.pagination !== 'none' &&
        text('maxPages', 'Máximo de páginas por sincronización', { type: 'number' })}

      <fieldset className="choice-group">
        <legend>Actualización</legend>
        {REFRESH.map((r) => {
          const allowed = r.hours === 0 || (planMin > 0 && r.hours >= planMin);
          const min =
            !allowed && ent
              ? minimumPlanFor(
                  ent.plans,
                  (v) =>
                    v['knowledge.api.minRefreshHours'] > 0 && r.hours >= v['knowledge.api.minRefreshHours'],
                )
              : null;
          return (
            <label key={r.value} className="choice" data-disabled={allowed ? undefined : true}>
              <input
                type="radio"
                name="refresh"
                disabled={!allowed}
                checked={form.refresh === r.value}
                onChange={() => onChange({ refresh: r.value })}
              />
              <span>
                <strong>{r.label}</strong>
              </span>
              {allowed ? (
                <span />
              ) : (
                <span className="chip">{min ? `Disponible desde ${PLAN_LABEL[min]}` : 'No incluido'}</span>
              )}
            </label>
          );
        })}
      </fieldset>

      <div className="stack" style={{ gap: 10 }}>
        <div>
          <Button icon="zap" loading={previewer.isPending} onClick={() => void runPreview()}>
            Probar conexión
          </Button>
        </div>
        {previewError && (
          <Alert tone="danger" title="No se pudo probar">
            {previewError}
          </Alert>
        )}
        {preview?.error && (
          <Alert tone="warning" title="La conexión respondió, pero hay un problema">
            {preview.error}
          </Alert>
        )}
        {preview && !preview.error && (
          <Alert
            tone="info"
            icon="check"
            title={`Conexión correcta: ${preview.total} elementos en la primera página`}
          >
            {preview.items.length ? (
              <ul className="preview-list">
                {preview.items.map((i) => (
                  <li key={i.id}>
                    <strong>{i.title}</strong>
                    <pre>{i.text || '(sin texto en los campos elegidos)'}</pre>
                  </li>
                ))}
              </ul>
            ) : (
              'La lista está vacía.'
            )}
          </Alert>
        )}
      </div>
    </>
  );
}
